import { useState, type ReactNode } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { ChipInput } from '@/components/ChipInput'

export interface RequiredKeywordGroupsProps {
  groups: string[][]
  onChange: (groups: string[][]) => void
  disabled?: boolean
  /** Phrases the last test run actually matched, across all groups (document-types only). */
  matchedPhrases?: string[]
  /** Aligned by index to `groups` - present once a test has run (document-types only). */
  groupHit?: boolean[]
  /** Singular noun for one item in a group, used in copy/placeholder text. Defaults to
   * document-types' original wording ("phrase") so that screen's own usage is unchanged. */
  itemNoun?: string
  /** Overrides the explanatory sentence above the groups entirely, if the default
   * (phrase-oriented) wording doesn't fit the caller's domain. */
  description?: ReactNode
  /** Fewest groups the last "remove" is allowed to leave behind. Document-types'
   * required_keywords_any_of is schema-required to have at least 1 group, hence the
   * default; case_data_requirements.must_have_any_of is entirely optional (real
   * fixtures omit it outright), so that caller passes 0. */
  minGroups?: number
}

// Originally PRD 03-specific (document-types' required_keywords_any_of); promoted here once
// PRD 02's case_data_requirements.rules.must_have_any_of needed the identical AND-across-
// groups/OR-within-group string[][] editor - same shape, same UI, only the item noun differs.
export function RequiredKeywordGroups({
  groups,
  onChange,
  disabled,
  matchedPhrases,
  groupHit,
  itemNoun = 'phrase',
  description,
  minGroups = 1,
}: RequiredKeywordGroupsProps) {
  // A group just created via "+ Add group" starts empty by definition - showing
  // "This group needs at least one X" before the operator has had any chance to
  // type something reads as a false alarm, not real feedback. Suppressed only for
  // a freshly-added group, and only until it either gains its first item (this
  // set entry is dropped, for good - re-emptying it afterward is a real, flaggable
  // state) or the operator moves focus away without adding one.
  const [pendingIndices, setPendingIndices] = useState<Set<number>>(new Set())

  function updateGroup(index: number, phrases: string[]) {
    onChange(groups.map((group, i) => (i === index ? phrases : group)))
    if (phrases.length > 0 && pendingIndices.has(index)) {
      setPendingIndices((current) => {
        const next = new Set(current)
        next.delete(index)
        return next
      })
    }
  }

  function addGroup() {
    setPendingIndices((current) => new Set(current).add(groups.length))
    onChange([...groups, []])
  }

  function removeGroup(index: number) {
    if (groups.length <= minGroups) return
    onChange(groups.filter((_, i) => i !== index))
    setPendingIndices((current) => {
      const next = new Set<number>()
      current.forEach((pendingIndex) => {
        if (pendingIndex < index) next.add(pendingIndex)
        else if (pendingIndex > index) next.add(pendingIndex - 1)
      })
      return next
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-meta text-text-secondary">
        {description ?? (
          <>
            A document must match at least one {itemNoun} in <em>every</em> group below (groups are
            combined with AND). Within a group, any one {itemNoun} is enough (they're combined with OR).
          </>
        )}
      </p>
      {groups.map((group, index) => {
        const isEmpty = group.length === 0
        const showEmptyError = isEmpty && !pendingIndices.has(index)
        const hitResult = groupHit?.[index]
        return (
          <div
            key={index}
            className="flex flex-col gap-1"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setPendingIndices((current) => {
                  if (!current.has(index)) return current
                  const next = new Set(current)
                  next.delete(index)
                  return next
                })
              }
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-meta font-medium text-text-secondary">
                Group {index + 1}
                {hitResult === false && <span className="ml-2 text-danger">No match</span>}
                {hitResult === true && <span className="ml-2 text-success">Matched</span>}
              </span>
              {!disabled && groups.length > minGroups && (
                <button
                  type="button"
                  aria-label={`Remove group ${index + 1}`}
                  onClick={() => removeGroup(index)}
                  className="text-text-secondary hover:text-danger"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
            <ChipInput
              value={group}
              onChange={(phrases) => updateGroup(index, phrases)}
              placeholder={`Type a ${itemNoun}, press Enter…`}
              disabled={disabled}
              reorderable
              invalid={showEmptyError}
              highlighted={matchedPhrases}
            />
            {showEmptyError && (
              <span className="text-meta text-danger">This group needs at least one {itemNoun}</span>
            )}
          </div>
        )
      })}
      {!disabled && (
        <button
          type="button"
          onClick={addGroup}
          className="inline-flex w-fit items-center gap-1 text-meta font-medium text-accent hover:underline"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add group
        </button>
      )}
    </div>
  )
}
